import React, { useState, useEffect } from 'react'

import {
    Chart,
    ChartLegend,
    ChartLegendItem,
    ChartSeries,
    ChartSeriesItem,
    ChartTitle,
    ChartTooltip,
} from '@progress/kendo-react-charts'
import { geometry, drawing } from '@progress/kendo-drawing'
// import { TextField } from '@material-ui/core'

import { MoneyInput } from '../../../GlobalComponents/Inputs/Inputs'

import styles from '../../TechPartnerDashboard/TechPartnerDashboard.module.scss'

import {
    fetchTechPartnerSalesByIndustryClass,
    fetchTechPartnerSalesByCustomerType,
    IndustryClassSalesData,
    CustomerTypeSalesData,
} from '../../../features/sales/salesAPI'

import { TechPartnerDashboardDataSectionProps } from '../TechPartnerDashboardSection'
import {
    fetchCustomerTypes,
    fetchIndustryClassifications,
} from '../../../features/customer/customerAPI'

export const MarketOpportunityData = (props: TechPartnerDashboardDataSectionProps) => {
    const [industryClassifications, setIndustryClassifications] =
        useState<IndustryClassificationView>([])
    const [customerTypes, setCustomerTypes] = useState<CustomerTypeView>([])

    const [nemaYTD, setNEMAYTD] = useState<number>(0)
    const [nemaPYTD, setNEMAPYTD] = useState<number>(0)
    const [nemaMarketDelta, setNEMAMarketDelta] = useState<number>(0)
    const [nemaMarketDeltaPercent, setNEMAMarketDeltaPercent] = useState<number>(0)
    const [availableMarketYTD, setAvailableMarketYTD] = useState<number>(0)
    const [availableMarketPYTD, setAvailableMarketPYTD] = useState<number>(0)
    const [servedMarketYTD, setServedMarketYTD] = useState<number>(0)
    const [servedMarketPYTD, setServedMarketPYTD] = useState<number>(0)
    const [servedMarketDeltaPercent, setServedMarketDeltaPercent] = useState<number>(0)
    const [servedMarketDelta, setServedMarketDelta] = useState<number>(0)
    const [percentOfMarketYTD, setPercentOfMarketYTD] = useState<number>(0)
    const [percentOfMarketPYTD, setPercentOfMarketPYTD] = useState<number>(0)
    const [industryClassYTD, setIndustryClassYTD] = useState<Array<IndustryClassSalesData>>([])
    const [industryClassPYTD, setIndustryClassPYTD] = useState<Array<IndustryClassSalesData>>([])
    const [customerTypeYTD, setCustomerTypeYTD] = useState<Array<CustomerTypeSalesData>>([])
    const [customerTypePYTD, setCustomerTypePYTD] = useState<Array<CustomerTypeSalesData>>([])
    const [customerTypeTotalYTD, setCustomerTypeTotalYTD] = useState<number>(0)
    const [customerTypeTotalPYTD, setCustomerTypeTotalPYTD] = useState<number>(0)

    const [refreshCharts, setRefreshCharts] = useState<boolean>(true)

    useEffect(() => {
        let dataCustomerTypes: CustomerTypeView
        let dataIndustryClasses: IndustryClassificationView

        async function getCustomerTypes() {
            dataCustomerTypes = await fetchCustomerTypes()
        }

        async function getIndustryClasses() {
            dataIndustryClasses = await fetchIndustryClassifications()
        }
        // run get functions in parallel, but for both to finish to avoid extra renders
        const asyncFunctions = [getCustomerTypes, getIndustryClasses]
        const getData = async () => {
            await Promise.all(
                asyncFunctions.map(async (get) => {
                    await get()
                })
            )
        }

        getData().then(() => {
            if (dataCustomerTypes) setCustomerTypes(dataCustomerTypes)
            if (dataIndustryClasses) setIndustryClassifications(dataIndustryClasses)
        })
    }, [])

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length === 0) return

        props.onLoadingChange(true)

        let dataIndustryClassYTD: Array<IndustryClassSalesData>
        let dataIndustryClassPYTD: Array<IndustryClassSalesData>
        async function getIndustryClassYTD() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataIndustryClassYTD = await fetchTechPartnerSalesByIndustryClass({
                ...props.selectedInputs,
                Period: 'ytd',
            })
        }
        async function getIndustryClassPYTD() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataIndustryClassPYTD = await fetchTechPartnerSalesByIndustryClass({
                ...props.selectedInputs,
                Period: 'pytd',
            })
        }

        let dataCustomerTypeYTD: Array<CustomerTypeSalesData>
        let dataCustomerTypePYTD: Array<CustomerTypeSalesData>
        async function getCustomerTypeYTD() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataCustomerTypeYTD = await fetchTechPartnerSalesByCustomerType({
                ...props.selectedInputs,
                Period: 'ytd',
            })
        }
        async function getCustomerTypePYTD() {
            if (Object.keys(props.selectedInputs).length === 0) return

            dataCustomerTypePYTD = await fetchTechPartnerSalesByCustomerType({
                ...props.selectedInputs,
                Period: 'pytd',
            })
        }

        // run get functions in parallel, but for both to finish to avoid extra renders
        const asyncFunctions = [
            getIndustryClassYTD,
            getIndustryClassPYTD,
            getCustomerTypeYTD,
            getCustomerTypePYTD,
        ]
        const getData = async () => {
            await Promise.all(
                asyncFunctions.map(async (get) => {
                    await get()
                })
            )
        }

        getData().then(() => {
            if (dataIndustryClassYTD) setIndustryClassYTD(dataIndustryClassYTD)
            if (dataIndustryClassPYTD) setIndustryClassPYTD(dataIndustryClassPYTD)
            if (dataCustomerTypeYTD) setCustomerTypeYTD(dataCustomerTypeYTD)
            if (dataCustomerTypePYTD) setCustomerTypePYTD(dataCustomerTypePYTD)
            setRefreshCharts(true)
        })
    }, [props.selectedInputs])

    useEffect(() => {
        if (Object.keys(props.selectedInputs).length > 0) calculateMarketOpportunity()

        props.onLoadingChange(false)
    }, [industryClassYTD, industryClassPYTD, customerTypeYTD, customerTypePYTD])

    const calculateMarketOpportunity = () => {
        setCustomerTypeTotalYTD(
            customerTypeYTD ? customerTypeYTD.reduce((a, b) => a + b.Sales, 0) : 0
        )
        setCustomerTypeTotalPYTD(
            customerTypePYTD ? customerTypePYTD.reduce((a, b) => a + b.Sales, 0) : 0
        )
    }

    const SharedTooltip = (props: any) => {
        const { category, value } = props.point

        return (
            <div>
                <div>{category}</div>
                <div>
                    {value.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    })}
                </div>
            </div>
        )
    }
    const sharedTooltipRender = (context: any) => <SharedTooltip {...context} />

    const renderIndustryClassRows = () => {
        let icRows = []
        for (let i = 0; i < industryClassifications.length; i++) {
            icRows.push(
                <React.Fragment key={`industryClassification${i}`}>
                    <div className={styles.dataLabel}>
                        {industryClassifications[i].IndustryName}
                    </div>
                    <div className={styles.dataCurrency}>
                        {/*Show $0 if no sales in industry*/}
                        {(industryClassYTD.find(
                            (c) =>
                                c.IndustryClassification === industryClassifications[i].IndustryName
                        )
                            ? industryClassYTD.find(
                                  (c) =>
                                      c.IndustryClassification ===
                                      industryClassifications[i].IndustryName
                              )?.Sales
                            : 0
                        )?.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {(industryClassPYTD.find(
                            (c) =>
                                c.IndustryClassification === industryClassifications[i].IndustryName
                        )
                            ? industryClassPYTD.find(
                                  (c) =>
                                      c.IndustryClassification ===
                                      industryClassifications[i].IndustryName
                              )?.Sales
                            : 0
                        )?.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    {i === 0 && (
                        <div
                            className={styles.chartContainer}
                            style={{
                                gridRow: `1 / ${
                                    Math.max(industryClassifications.length, customerTypes.length) +
                                    1
                                }`,
                            }}
                        >
                            <Chart
                                style={{ height: '700px', width: '100%' }}
                                transitions={!props.loading}
                                onRefresh={handleChartRefresh}
                            >
                                <ChartTitle
                                    text='Industry Classifications'
                                    align='left'
                                    font='bold 12px Roboto'
                                />
                                <ChartTooltip shared={true} render={sharedTooltipRender} />
                                <ChartSeries>
                                    <ChartSeriesItem
                                        type='pie'
                                        data={industryClassYTD.sort((a, b) => b.Sales - a.Sales)}
                                        field='Sales'
                                        categoryField='IndustryClassification'
                                        padding={10}
                                    />
                                </ChartSeries>
                                <ChartLegend position='bottom' align='center'>
                                    <ChartLegendItem
                                        visual={(e) => {
                                            var color = e.options.markers.background
                                            var labelColor = e.options.labels.color
                                            var rect = new geometry.Rect([0, 0], [200, 50])
                                            var layout = new drawing.Layout(rect, {
                                                spacing: 5,
                                                alignItems: 'center',
                                            })

                                            var squareGeometry = new geometry.Rect([0, 0], [7, 7])
                                            var square = new drawing.Rect(squareGeometry, {
                                                stroke: { color: 'none' },
                                                fill: { color: color },
                                            })

                                            var label = new drawing.Text(
                                                e.series.data[e.pointIndex].IndustryClassification,
                                                [0, 0],
                                                {
                                                    fill: { color: labelColor },
                                                    opacity: 0.99995,
                                                }
                                            )

                                            var rectMargin = new drawing.Rect(
                                                new geometry.Rect([0, 0], [30, 30]),
                                                {
                                                    stroke: { color: 'none' },
                                                    fill: { color: 'none' },
                                                }
                                            )

                                            layout.append(square, label, rectMargin)
                                            layout.reflow()

                                            return layout
                                        }}
                                    />
                                </ChartLegend>
                            </Chart>
                        </div>
                    )}
                </React.Fragment>
            )
        }
        return icRows
    }

    const renderCustomerTypeRows = () => {
        let icRows = []
        for (let i = 0; i < customerTypes.length; i++) {
            icRows.push(
                <React.Fragment key={`customerType${i}`}>
                    <div className={styles.dataLabel}>{customerTypes[i].Name}</div>
                    <div className={styles.dataCurrency}>
                        {(customerTypeYTD?.find((c) => c.CustomerType === customerTypes[i].Name)
                            ? customerTypeYTD?.find((c) => c.CustomerType === customerTypes[i].Name)
                                  ?.Sales
                            : 0
                        )?.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={styles.dataCurrency}>
                        {(customerTypePYTD?.find((c) => c.CustomerType === customerTypes[i].Name)
                            ? customerTypePYTD?.find(
                                  (c) => c.CustomerType === customerTypes[i].Name
                              )?.Sales
                            : 0
                        )?.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    {i === 0 && (
                        <div
                            className={styles.chartContainer}
                            style={{
                                gridRow: `1 / ${Math.max(
                                    industryClassifications.length,
                                    customerTypes.length
                                )}`,
                            }}
                        >
                            <Chart
                                style={{ minHeight: '550px', width: '100%' }}
                                transitions={!props.loading}
                                onRefresh={handleChartRefresh}
                            >
                                <ChartTitle
                                    text='Customer Type'
                                    align='left'
                                    font='bold 12px Roboto'
                                />
                                <ChartTooltip shared={true} render={sharedTooltipRender} />
                                <ChartSeries>
                                    <ChartSeriesItem
                                        type='pie'
                                        data={customerTypeYTD.sort((a, b) => b.Sales - a.Sales)}
                                        field='Sales'
                                        categoryField='CustomerType'
                                        padding={10}
                                    />
                                </ChartSeries>
                                <ChartLegend position='bottom' align='center'>
                                    <ChartLegendItem
                                        visual={(e) => {
                                            var color = e.options.markers.background
                                            var labelColor = e.options.labels.color
                                            var rect = new geometry.Rect([0, 0], [300, 50])
                                            var layout = new drawing.Layout(rect, {
                                                spacing: 5,
                                                alignItems: 'center',
                                            })

                                            var squareGeometry = new geometry.Rect([0, 0], [7, 7])
                                            var square = new drawing.Rect(squareGeometry, {
                                                stroke: { color: 'none' },
                                                fill: { color: color },
                                            })

                                            var label = new drawing.Text(
                                                e.series.data[e.pointIndex].CustomerType,
                                                [0, 0],
                                                {
                                                    fill: { color: labelColor },
                                                    opacity: 0.99995,
                                                }
                                            )

                                            var rectMargin = new drawing.Rect(
                                                new geometry.Rect([0, 0], [30, 30]),
                                                {
                                                    stroke: { color: 'none' },
                                                    fill: { color: 'none' },
                                                }
                                            )

                                            layout.append(square, label, rectMargin)
                                            layout.reflow()

                                            return layout
                                        }}
                                    />
                                </ChartLegend>
                            </Chart>
                        </div>
                    )}
                </React.Fragment>
            )
        }
        return icRows
    }

    const handleChartRefresh = (chartOptions: any, themeOptions: any, chartInstance: any) => {
        if (refreshCharts) {
            chartInstance.setOptions(chartOptions, themeOptions)
        }
    }

    return (
        <div className={styles.marketOpportunity} id='market-opportunity'>
            <div className={`${styles.dataContainer} ${styles.markets}`}>
                <div className={styles.titleRow}>
                    <div className={styles.columnHeader}></div>
                    <div className={`${styles.columnHeader} ${styles.dataCurrency}`}>YTD</div>
                    <div className={`${styles.columnHeader} ${styles.dataCurrency}`}>PYTD</div>
                    <div className={`${styles.columnHeader} ${styles.dataPercent}`}>Delta %</div>
                    <div className={`${styles.columnHeader} ${styles.dataCurrency}`}>Delta $</div>
                </div>
                <div className={styles.dataRows}>
                    <div
                        className={styles.dataLabel}
                        title='Enter the latest NEMA numbers for a specific range of products for the region being reviewed.'
                    >
                        NEMA Market
                    </div>
                    <div className={styles.dataCurrency}>
                        <MoneyInput
                            useNativeOnChange={true}
                            thousandSeparator={true}
                            id='NEMAYTD'
                            variant='outlined'
                            validate={false}
                            tabIndex={5}
                            onBlur={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                                e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                let numVal: number =
                                    e.target.value === '' ? 0 : parseFloat(e.target.value)

                                setNEMAYTD(numVal)

                                setNEMAMarketDeltaPercent(nemaPYTD == 0 ? 0 : numVal / nemaPYTD)
                                setNEMAMarketDelta(numVal - nemaPYTD)

                                if (numVal !== 0) setPercentOfMarketYTD(servedMarketYTD / numVal)

                                if (e.target.value !== '')
                                    e.target.value = numVal.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })

                                setRefreshCharts(false)
                            }}
                            onFocus={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => e.target.select()}
                        />
                    </div>
                    <div className={styles.dataCurrency}>
                        <MoneyInput
                            id='NEMAPYTD'
                            variant='outlined'
                            validate={false}
                            tabIndex={6}
                            onBlur={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                                e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                let numVal: number =
                                    e.target.value === '' ? 0 : parseFloat(e.target.value)

                                setNEMAPYTD(numVal)
                                setNEMAMarketDeltaPercent(numVal == 0 ? 0 : nemaYTD / numVal)
                                setNEMAMarketDelta(nemaYTD - numVal)
                                if (numVal !== 0) setPercentOfMarketPYTD(servedMarketPYTD / numVal)
                                if (e.target.value !== '')
                                    e.target.value = numVal.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })
                                setRefreshCharts(false)
                            }}
                            onFocus={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => e.target.select()}
                        />
                    </div>
                    <div className={styles.dataPercent}>
                        {nemaPYTD !== 0 &&
                            nemaMarketDeltaPercent.toLocaleString('en-US', {
                                style: 'percent',
                                currency: 'USD',
                            })}
                    </div>
                    <div
                        className={`${styles.dataCurrency}${
                            nemaMarketDelta < 0 ? ` ${styles.negative}` : ''
                        }`}
                    >
                        {nemaMarketDelta.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div></div>
                    {/* Row 3 */}
                    <div
                        className={styles.dataLabel}
                        title='The market potential for the region that can be covered by the products/technology available from our tech partner.'
                    >
                        Available Market
                    </div>
                    <div className={styles.dataCurrency}>
                        <MoneyInput
                            id='availableMarketYTD'
                            variant='outlined'
                            validate={false}
                            tabIndex={7}
                            onBlur={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                                e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                let numVal: number =
                                    e.target.value === '' ? 0 : parseFloat(e.target.value)

                                setAvailableMarketYTD(numVal)
                                if (e.target.value !== '')
                                    e.target.value = numVal.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })
                                setRefreshCharts(false)
                            }}
                            onFocus={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => e.target.select()}
                        />
                    </div>
                    <div className={styles.dataCurrency}>
                        <MoneyInput
                            id='availableMarketPYTD'
                            variant='outlined'
                            validate={false}
                            tabIndex={8}
                            onBlur={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                                e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                let numVal: number =
                                    e.target.value === '' ? 0 : parseFloat(e.target.value)

                                setAvailableMarketPYTD(numVal)
                                if (e.target.value !== '')
                                    e.target.value = numVal.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })
                                setRefreshCharts(false)
                            }}
                            onFocus={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => e.target.select()}
                        />
                    </div>
                    <div></div>
                    <div></div>
                    <div></div>
                    {/* Row 4 */}
                    <div
                        className={styles.dataLabel}
                        title="The served market is the market that 'we' are currently focused on and can serve/support. Example: we may have only one salesperson and one app engr to cover a market that could support four sales staff and four app engineers. Our Served Market would be 1/4 of the 'Available Market'."
                    >
                        Served Market
                    </div>
                    <div className={styles.dataCurrency}>
                        <MoneyInput
                            id='servedMarketYTD'
                            variant='outlined'
                            validate={false}
                            tabIndex={9}
                            onBlur={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                                e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                let numVal: number =
                                    e.target.value === '' ? 0 : parseFloat(e.target.value)

                                setServedMarketYTD(numVal)
                                setServedMarketDeltaPercent(
                                    nemaPYTD == 0 ? 0 : numVal / servedMarketPYTD
                                )
                                setServedMarketDelta(numVal - servedMarketPYTD)
                                if (nemaYTD !== 0) setPercentOfMarketYTD(numVal / nemaYTD)
                                if (e.target.value !== '')
                                    e.target.value = numVal.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })
                                setRefreshCharts(false)
                            }}
                            onFocus={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => e.target.select()}
                        />
                    </div>
                    <div className={styles.dataCurrency}>
                        <MoneyInput
                            id='servedMarketPYTD'
                            variant='outlined'
                            validate={false}
                            tabIndex={10}
                            onBlur={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                                e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                let numVal: number =
                                    e.target.value === '' ? 0 : parseFloat(e.target.value)

                                setServedMarketPYTD(numVal)
                                setServedMarketDeltaPercent(
                                    numVal == 0 ? 0 : servedMarketYTD / numVal
                                )
                                setServedMarketDelta(servedMarketYTD - numVal)
                                if (nemaPYTD !== 0) setPercentOfMarketPYTD(numVal / nemaPYTD)
                                if (e.target.value !== '')
                                    e.target.value = numVal.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })
                                setRefreshCharts(false)
                            }}
                            onFocus={(
                                e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => e.target.select()}
                        />
                    </div>
                    <div className={styles.dataPercent}>
                        {servedMarketPYTD !== 0 &&
                            servedMarketDeltaPercent.toLocaleString('en-US', {
                                style: 'percent',
                                currency: 'USD',
                            })}
                    </div>
                    <div
                        className={`${styles.dataCurrency}${
                            servedMarketDelta < 0 ? ` ${styles.negative}` : ''
                        }`}
                    >
                        {servedMarketDelta.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                </div>
                {/* Row 5 */}
                <div className={styles.totalRow}>
                    <div className={styles.dataLabel}>% of Market</div>
                    <div className={styles.dataPercent}>
                        {nemaYTD !== 0 &&
                            percentOfMarketYTD.toLocaleString('en-US', {
                                style: 'percent',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                    </div>
                    <div className={styles.dataPercent}>
                        {nemaPYTD !== 0 &&
                            percentOfMarketPYTD.toLocaleString('en-US', {
                                style: 'percent',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                    </div>
                    <div></div>
                    <div></div>
                </div>
            </div>
            <div className={`${styles.dataContainer} ${styles.industryClass}`}>
                {/* Row 1 */}
                <div className={styles.titleRow}>
                    <div className={`${styles.columnHeader} ${styles.alignLeft}`}>Industry</div>
                    <div className={`${styles.columnHeader} ${styles.dataCurrency}`}>YTD</div>
                    <div className={`${styles.columnHeader} ${styles.dataCurrency}`}>PYTD</div>
                    <div className={styles.columnHeader}></div>
                </div>
                <div className={styles.dataRows}>{renderIndustryClassRows()}</div>
            </div>
            <div className={`${styles.dataContainer} ${styles.customerType}`}>
                {/* Row 1 */}
                <div className={styles.titleRow}>
                    <div className={`${styles.columnHeader} ${styles.alignLeft}`}>
                        Customer Type
                    </div>
                    <div className={styles.columnHeader}>YTD</div>
                    <div className={styles.columnHeader}>PYTD</div>
                    <div className={styles.columnHeader}></div>
                </div>
                <div className={styles.dataRows}>{renderCustomerTypeRows()}</div>
                <div className={styles.totalRow}>
                    <div className={styles.dataLabel}>Total</div>
                    <div className={`${styles.bordered} ${styles.dataPercent}`}>
                        {customerTypeTotalYTD.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div className={`${styles.bordered} ${styles.dataPercent}`}>
                        {customerTypeTotalPYTD.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })}
                    </div>
                    <div></div>
                </div>
            </div>
        </div>
    )
}

export default MarketOpportunityData
